import { Box, Typography, Tooltip, Stack, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import ToolTipIcon from '../../assets/info-icon.svg';
import LoaderComponent from 'components/Loader';
import { ECElementEvent } from 'echarts';
import { useEffect, useRef, useState } from 'react';

interface ChartData {
    name: string;
    value: number;
    valueInBytes: number;
    unit: string;
}

interface TopStorageAccountsChartProps {
    isLoading: boolean;
    title?: string;
    tooltipLabel?: string;
    data: ChartData[];
    handleEvent?: (name: string) => void;
    clusterStorage?:boolean;
}

const ProgressHorizontalBarChart = ({
    isLoading = false,
    title = '',
    tooltipLabel = '',
    data = [],
    handleEvent,
    
    clusterStorage
}: TopStorageAccountsChartProps) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [windowSize, setWindowSize] = useState(window.innerWidth);
    const [gridLeft, setGridLeft] = useState('5%');

    useEffect(() => {
        const handleResize = debounce(() => {
            if (chartContainerRef.current) {
                const containerWidth = chartContainerRef.current.offsetWidth;

                let calculatedLeft: string;

                if (containerWidth < 420) {
                    calculatedLeft = '28%';
                } else if (containerWidth < 600) {
                    calculatedLeft = '20%';
                } else if (containerWidth < 820) {
                    calculatedLeft = '18%';
                } else if (containerWidth < 920) {
                    calculatedLeft = '13%';
                } else if (containerWidth < 1260) {
                    calculatedLeft = '10%';
                } else {
                    calculatedLeft = '10%';
                }

                setGridLeft(calculatedLeft);
                setWindowSize(window.innerWidth);
            }
        }, 200);

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getChartOptions = (data: ChartData[]) => {
        const reversedData = [...data].reverse();
        const maxValue = Math.max(...data.map(item => item.value));
        const barWidth = data.length > 20 ? 8 : 20;

        // Truncate Y-axis label
        const truncateLabel = (str: string, maxLength = 15) =>
            str.length > maxLength ? str.slice(0, maxLength) + '…' : str;
    
        return {
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#003771',
                textStyle: { color: '#A9ADCB' },
                formatter: function (params: any) {
                    const item = params[0];
                    const name = item.name;
                    const value = item.value;
                    const unit = item.data.unit || '';
                    return `${item.marker} ${name} : ${value} ${unit}`;
                },
              },
            xAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value: number) => {
                    return value % 1 === 0 ? `${value.toFixed(0)}` : `${value.toFixed(1)}`;
                },
                    color: '#A9ADCB',
                    rotate: window.innerWidth < 600 ? 30 : 0, 
                },
                splitLine: {
                    show: false,
                },
                min: 0,
                max: Math.ceil(maxValue),
                // interval: window.innerWidth < 800 ? 1 : 0.5,
                show: false,
            },
            yAxis: {
                type: 'category',
                data: reversedData.map((item) => item.name),
                axisLine: {
                    show: false,
                },
                splitLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    color: '#A9ADCB',
                    formatter: (name: string) => truncateLabel(name),
                },
            },
            series: [
                {
                    type: 'bar',
                    barWidth: barWidth,
                    showBackground: true,
				    backgroundStyle: {
					color: '#174377'
				},
                // data: reversedData.map((item) => ({
                //     value: item.value,
                //     unit: item.unit,
                // })),
                data: reversedData,
                itemStyle: {
                    color: '#1E88E5',
                },
                barGap: '20%',
                },
            ],
            grid: {
                right: '2%',
                left: gridLeft,
                bottom: '14%',
                top: '1%',
            },
        };
    };    

    const adjustedOption = data.length ? getChartOptions(data) : {};

    return (
        <Box display="flex" flexGrow={1} height="100%" position="relative">
            <Stack
                minHeight="100px"
                p={2}
                bgcolor="primary.main"
                fontFamily="Montserrat"
                borderRadius="5px"
                flexGrow={1}
            >
                {title && (
                    <Stack direction="row" gap={2} justifyContent="space-between" alignItems="start" minHeight="50.5px">
                        <Stack direction="row" gap={1.5} alignItems="start">
                            <Typography sx={{ fontSize: {xs: clusterStorage ? '12px' : '18px', lg: '20px'} }} fontWeight={500}>{title}</Typography>
                            {tooltipLabel && (
                                <Tooltip title={tooltipLabel} arrow>
                                    <img src={ToolTipIcon} alt="tooltip" />
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack>
                 )}
                <Stack minHeight={clusterStorage? "280px":"600px"} flexGrow={1} key={windowSize} justifyContent="center" ref={chartContainerRef}>
                    {!isLoading ? (
                        <ReactECharts
                            style={{ width: '100%', height: '100%' }}
                            option={adjustedOption}
                            onEvents={{
                                click: (params: ECElementEvent) => {
                                    if (handleEvent && data.length) {
                                        const clickedData = params.data as {
                                            name: string;
                                            value: number;
                                            valueInBytes: number;
                                            unit: string;
                                            item: any;
                                        };
                                        const clickedRow = clickedData?.item;
                                        if (!clickedRow) return;

                                        const isClickable =
                                            clickedRow.isFile !== 1 &&
                                            (typeof clickedRow.level !== 'number' || clickedRow.level < 5) && clickedRow.lastLevel !== true;

                                        if (isClickable) {
                                            const clickedName = params.name;
                                            handleEvent(clickedName);
                                        }
                                    }
                                },
                            }}
                            opts={{ renderer: 'canvas' }}
                            notMerge={true}
                            lazyUpdate={true}
                        />
                    ) : (
                        <LoaderComponent />
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default ProgressHorizontalBarChart;